/**
 * CALL THROUGH THE BUSINESS LINE (ops #84 phase 6, `docs/plan-client-workplace.md` section 9).
 *
 * Beside a person's tap-to-call on the client's page and the home's page, for the office: the phone
 * system rings the presser's own phone first, says whom it is for, and dials the client only once a
 * key is pressed there, so the client sees (727) 337-7775 and never a personal number. `CallLink`'s
 * `tel:` link stays beside it for a handset outside the software.
 *
 * Ops dials nothing: the press asks the server, which records the request and answers at once, and
 * the page polls where it stands, six times on the call page's delays (`POLL_DELAYS_MS`), drawing
 * each state in words. After the sixth the banner takes over, "On the line", from the phone system's
 * own `call.started`.
 *
 * WHILE THE LINE IS NOT SWITCHED ON FOR OPS (`me.placeCalls` false or absent), the button is drawn
 * disabled with the sentence that says so, and nothing is asked. A person not to be called is never
 * offered it: `CallLink` already writes "Do not call" in place of the number. Every one of these is UX;
 * the server refuses in words whatever is drawn.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { ApiError, getCallRequest, placeCall, type CallRequestView } from '../api.ts';
import { callLengthWords } from '../format.ts';
import { POLL_DELAYS_MS } from '../screens/CallDetail.tsx';
import { Button } from './Field.tsx';

/** The first word of a name, as the line says it to the presser. */
const firstOf = (name: string): string => name.trim().split(/\s+/)[0] || 'them';

/** What the button says beside it while the line is on: the phone system's FR-010 key press. */
export const RINGS_FIRST = 'Your phone rings first; press any key to connect.';

/** What it says while the line is not switched on for ops. */
export const NOT_SWITCHED_ON =
  'The business line is not switched on for calls from ops yet. Call from your own phone meanwhile.';

/** The sentence for a refusal of the phone system's, by its word. */
function refusedWords(view: CallRequestView, first: string): string {
  const said = view.phoneSaid ? ` It said: ${view.phoneSaid}` : '';
  switch (view.code) {
    case 'not_enabled':
      return 'The phone system has not switched placing calls on yet, so nothing rang.';
    case 'no_handset':
      return 'The phone system has no phone of yours to ring first. Ask the owner to add one there. Nothing rang.';
    case 'number_blocked':
      return `The phone system does not call ${first}'s number: it is on its block list, or stopped for calls. Nothing rang.`;
    case 'line_busy':
      return 'Your phone already has a call placed through the line. One at a time: finish that one first. Nothing rang.';
    case 'limit_reached':
      return `The phone system's limit on calls is reached for now, so nothing rang.${said}`;
    case 'expired':
      return 'It reached the phone system too late, so nothing rang. Press Call again if you still want to call.';
    case 'key_reused':
      return 'The phone system took it for another call, so nothing rang. Press Call again.';
    default:
      return `The phone system refused it, so nothing rang.${said}`;
  }
}

/** Where a request stands, in the words the office reads. */
export function callWords(view: CallRequestView, name: string): string {
  const first = firstOf(name);
  if (view.call) {
    const length = callLengthWords(view.call.durationSeconds);
    switch (view.call.outcome) {
      case null:
        return `On the line with ${first}.`;
      case 'answered':
        return `Spoke with ${first}${length ? `, ${length}` : ''}.`;
      case 'missed':
        return `The call did not reach ${first}: no answer, a busy line or a number that could not be reached.`;
      case 'abandoned':
        return `No key was pressed on your phone, so ${first}'s phone never rang.`;
      case 'voicemail':
        return `${first}'s voicemail took the call.`;
      default:
        return `The call with ${first} has ended.`;
    }
  }
  switch (view.state) {
    case 'asking':
      return 'Asking the phone system.';
    case 'ringing':
      return `The phone system is ringing your phone. Answer it and press any key to connect to ${first}.`;
    case 'not_confirmed':
      return 'The phone system has not confirmed it yet. If your phone rings, it is this call.';
    case 'expired':
      return view.attempted
        ? 'The phone system never confirmed it within the minute. If your phone did not ring, press Call again.'
        : 'The phone system could not be reached within the minute, so nothing rang. Press Call again if you still want to call.';
    case 'refused':
      return refusedWords(view, first);
  }
}

/** A state the polls stop at: nothing more will change it from here. */
const settled = (view: CallRequestView): boolean =>
  view.state === 'refused' ||
  view.state === 'expired' ||
  (view.call !== null && view.call.outcome !== null);

export function CallThrough({
  clientId,
  contactId,
  name,
  on,
}: {
  clientId: string;
  contactId: string;
  /** The person's name as the page shows it; the first word is said back. */
  name: string;
  /** `me.placeCalls`: the line is switched on for ops on this deployment. */
  on: boolean;
}) {
  const noteId = useId();
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  /** Which press is current; an answer from an older one is dropped. */
  const round = useRef(0);

  useEffect(
    () => () => {
      round.current += 1;
      for (const timer of timers.current) clearTimeout(timer);
    },
    [],
  );

  /** The polls, one after another, each only once the one before has answered. */
  function pollFrom(index: number, requestId: string, mine: number) {
    const delay = POLL_DELAYS_MS[index];
    if (delay === undefined) {
      if (mine === round.current) setBusy(false);
      return;
    }
    timers.current.push(
      setTimeout(() => {
        getCallRequest(clientId, requestId)
          .then((view) => {
            if (mine !== round.current) return;
            setSaid(callWords(view, name));
            if (settled(view)) setBusy(false);
            else pollFrom(index + 1, requestId, mine);
          })
          .catch(() => {
            if (mine !== round.current) return;
            pollFrom(index + 1, requestId, mine);
          });
      }, delay),
    );
  }

  const press = () => {
    round.current += 1;
    const mine = round.current;
    setBusy(true);
    setSaid('Asking the phone system.');
    placeCall(clientId, contactId)
      .then(({ requestId }) => {
        if (mine === round.current) pollFrom(0, requestId, mine);
      })
      .catch((thrown: unknown) => {
        if (mine !== round.current) return;
        setBusy(false);
        setSaid(
          thrown instanceof ApiError ? thrown.message : 'The call was not asked for. Try again.',
        );
      });
  };

  return (
    <div className="flex flex-col gap-1">
      <div>
        <Button tone="quiet" onClick={press} disabled={!on || busy} describedBy={noteId}>
          Call through the business line
        </Button>
      </div>
      <p id={noteId} className="text-[13px] leading-[18px] text-navy/70">
        {on ? RINGS_FIRST : NOT_SWITCHED_ON}
      </p>
      {said ? (
        <p role="status" aria-live="polite" className="text-sm">
          {said}
        </p>
      ) : null}
    </div>
  );
}
